/**
 * Animated particle network background (login / hero pages).
 * Reference: docs/topography-background/init-bg-canvas.js
 */

/**
 * @param {string} [canvasId]
 * @returns {(() => void) | null}
 */
export function initBgCanvas(canvasId = 'canvas') {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return null;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const resize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };

  resize();
  window.addEventListener('resize', resize);

  const FPS = 60;
  const CONNECTION_DISTANCE = 150;
  const PARTICLE_COLOR = '#4ade80';
  const LINE_COLOR = 'gray';
  const PARTICLE_COUNT = 100;

  const mouse = { x: 0, y: 0 };

  const stars = Array.from({ length: PARTICLE_COUNT }, () => ({
    x: Math.random() * canvas.width,
    y: Math.random() * canvas.height,
    radius: Math.random() * 1 + 1,
    vx: Math.floor(Math.random() * 50) - 25,
    vy: Math.floor(Math.random() * 50) - 25,
  }));

  /**
   * @param {{ x: number, y: number }} a
   * @param {{ x: number, y: number }} b
   */
  function distance(a, b) {
    const xs = (b.x - a.x) ** 2;
    const ys = (b.y - a.y) ** 2;
    return Math.sqrt(xs + ys);
  }

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'lighter';

    for (const s of stars) {
      ctx.fillStyle = PARTICLE_COLOR;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.beginPath();
    for (const starI of stars) {
      ctx.moveTo(starI.x, starI.y);
      if (distance(mouse, starI) < CONNECTION_DISTANCE) {
        ctx.lineTo(mouse.x, mouse.y);
      }
      for (const starII of stars) {
        if (distance(starI, starII) < CONNECTION_DISTANCE) {
          ctx.lineTo(starII.x, starII.y);
        }
      }
    }
    ctx.lineWidth = 0.1;
    ctx.strokeStyle = LINE_COLOR;
    ctx.stroke();
  }

  function update() {
    for (const s of stars) {
      s.x += s.vx / FPS;
      s.y += s.vy / FPS;
      if (s.x < 0 || s.x > canvas.width) s.vx = -s.vx;
      if (s.y < 0 || s.y > canvas.height) s.vy = -s.vy;
    }
  }

  const onMouseMove = (e) => {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  };

  canvas.addEventListener('mousemove', onMouseMove);

  let frameId = 0;
  function tick() {
    draw();
    update();
    frameId = requestAnimationFrame(tick);
  }
  tick();

  return () => {
    cancelAnimationFrame(frameId);
    window.removeEventListener('resize', resize);
    canvas.removeEventListener('mousemove', onMouseMove);
  };
}
